import { createSelector } from '@reduxjs/toolkit';
import { BUSINESS_TYPE } from 'LixaniAPI/enums';
import {
  selectSelectedProject,
  selectSelectedProjectId,
} from 'store/projects/selectors/projectsSelectors';

const selectAssignmentsState = (state) =>
  state.expenseTracking.projectAssignments;

// projectId -> 'loading' | 'loaded' | 'failed'. Entries in projectAssignments
// alone don't mean a project is loaded: saving budgets or litterat writes a
// few of a project's litterat there too.
export const selectAssignmentLoadStatusByProject = (state) =>
  state.expenseTracking.assignmentLoadStatusByProject;

// Company mode loads every littera of the company at once.
export const selectAllProjectAssignmentsLoaded = (state) =>
  state.expenseTracking.allProjectAssignmentsLoaded;

// The page waits for the litterat it shows up front: the whole company's in
// company mode, the open project's otherwise. A failed load doesn't hold it.
export const selectArePageAssignmentsLoaded = createSelector(
  [
    selectSelectedProject,
    selectAssignmentLoadStatusByProject,
    selectAllProjectAssignmentsLoaded,
  ],
  (project, statusByProject, allProjectAssignmentsLoaded) => {
    if (project?.businessTypeEnum === BUSINESS_TYPE.EMPLOYER) {
      return allProjectAssignmentsLoaded;
    }
    const status = statusByProject[project?.id];
    return status === 'loaded' || status === 'failed';
  }
);

const selectCompaniesState = (state) => state.expenseTracking.companies;

export const selectLoadingCompanies = (state) =>
  state.expenseTracking.loadingCompanies;

export const selectLoadingUpdateCompanyName = (state) =>
  state.expenseTracking.loadingUpdateCompanyName;

export const selectCompaniesArray = createSelector(
  [selectCompaniesState],
  (companies) => Object.values(companies?.entities || {})
);

export const selectCompaniesByBid = createSelector(
  [selectCompaniesArray, selectSelectedProjectId],
  (companies, selectedProjectId) => {
    return companies.reduce((acc, company) => {
      const company_bid = company.company_bid;
      if (!company_bid) {
        return acc;
      }
      const existing = acc[company_bid];
      if (!existing) {
        acc[company_bid] = company;
        return acc;
      }
      if (
        company.project === selectedProjectId &&
        existing.project !== selectedProjectId
      ) {
        acc[company_bid] = company;
      }
      return acc;
    }, {});
  }
);

const selectAssignmentCostsState = (state) =>
  state.expenseTracking.assignmentCosts;

const selectParentAndSubProjectsState = (state) =>
  state.expenseTracking.parentAndSubProjects;

const selectEmployersState = (state) => state.expenseTracking.employers;
const selectAdditionalWorksState = (state) =>
  state.expenseTracking.additionalWorks;

export const selectLoadingAdditionalWorks = (state) =>
  state.expenseTracking.loadingAdditionalWorks;

export const selectSettingsState = (state) => state.expenseTracking.settings;

export const selectEmployers = createSelector(
  [selectEmployersState],
  (state) => state.entities
);

const selectResourcesState = (state) => state.expenseTracking.resources;

export const selectResources = createSelector(
  [selectResourcesState],
  (state) => state.entities
);

export const selectAdditionalWorks = createSelector(
  [selectAdditionalWorksState],
  (state) => state?.entities || {}
);

export const selectAdditionalWorksArray = createSelector(
  [selectAdditionalWorks],
  (additionalWorks) => Object.values(additionalWorks)
);

const isAdditionalWorkType = (additionalWork, expectedType) => {
  const normalizedType =
    additionalWork?.type === 'SHAREHOLDER_CHANGE' ||
    additionalWork?.type === 'shareholderChange'
      ? 'shareholderChange'
      : 'additionalWork';

  if (expectedType === 'additionalWork') {
    return !additionalWork?.type || normalizedType === expectedType;
  }

  return normalizedType === expectedType;
};

export const selectAdditionalWorkItems = createSelector(
  [selectAdditionalWorksArray],
  (additionalWorks) =>
    additionalWorks.filter((additionalWork) =>
      isAdditionalWorkType(additionalWork, 'additionalWork')
    )
);

export const selectShareholderChangeItems = createSelector(
  [selectAdditionalWorksArray],
  (additionalWorks) =>
    additionalWorks.filter((additionalWork) =>
      isAdditionalWorkType(additionalWork, 'shareholderChange')
    )
);

const selectCostReportsState = (state) => state.expenseTracking.costReports;

export const selectLoadingCostReports = (state) =>
  state.expenseTracking.loadingCostReports;

export const selectLoadingGenerateCostReport = (state) =>
  state.expenseTracking.loadingGenerateCostReport;

export const selectCostReports = createSelector(
  [selectCostReportsState],
  (state) => state?.entities || {}
);

export const selectCostReportItems = createSelector(
  [selectCostReports],
  (costReports) =>
    Object.values(costReports).sort((a, b) =>
      (b.createdAt || '').localeCompare(a.createdAt || '')
    )
);

export const selectProjectAssignments = createSelector(
  [selectAssignmentsState],
  (state) => state.entities
);

export const selectLoadingSaveAssignmentBudgets = (state) =>
  Boolean(state.expenseTracking.loadingSaveAssignmentBudgets);

export const selectAssignmentCosts = createSelector(
  [selectAssignmentCostsState],
  (state) => state.entities
);

export const selectAssignmentCostTotal = createSelector(
  [selectAssignmentCosts],
  (assignmentWithCost) =>
    Object.values(assignmentWithCost).reduce(
      (total, assignment) => total + (parseFloat(assignment.workCost) || 0),
      0
    )
);

export const selectAssignmentOtherCostTotal = createSelector(
  [selectAssignmentCosts],
  (assignmentWithCost) =>
    Object.values(assignmentWithCost).reduce(
      (total, assignment) => total + (parseFloat(assignment.otherCost) || 0),
      0
    )
);

export const selectProjectAssignmentTree = createSelector(
  [selectProjectAssignments],
  (assignments) =>
    Object.values(assignments).reduce((x, y) => {
      (x[y.project] = x[y.project] || []).push(y);
      return x;
    }, {})
);

const NO_ASSIGNMENTS = [];

// The open project's own litterat. Compare with shallowEqual: the list is
// rebuilt whenever any project's litterat load, its items only when its own do.
export const selectOpenProjectAssignments = createSelector(
  [selectProjectAssignmentTree, selectSelectedProjectId],
  (projectAssignmentTree, projectId) =>
    projectAssignmentTree[projectId] || NO_ASSIGNMENTS
);

export const selectParentAndSubProjects = createSelector(
  [selectParentAndSubProjectsState],
  (state) => state.entities
);

export const selectResourcesWithCosts = createSelector(
  [selectResources],
  (resources) => {
    return countResourceCosts(Object.values(resources));
  }
);

export const selectEmployersWithCosts = createSelector(
  [selectEmployers, selectResourcesWithCosts],
  (employers, resources) => {
    const fallbackEmployer = {
      id: 'MyLixani',
      name: 'MyLixani',
    };
    const employersWithFallback = { ...employers, fallbackEmployer };
    return countEmployerHours(Object.values(employersWithFallback), resources);
  }
);

export const selectEmployerResourceTree = createSelector(
  [selectEmployersWithCosts, selectResourcesWithCosts],
  (employers, resources) => {
    if (!employers || !resources) {
      return [];
    }
    // Group resources by employerBusinessId

    const resourcesByEmployerBusinessId = resources.reduce((acc, resource) => {
      const employerBusinessId = resource.employerBusinessId; // Use fallback ID for unassigned resources
      if (!acc[employerBusinessId]) {
        acc[employerBusinessId] = [];
      }
      acc[employerBusinessId].push(resource);
      return acc;
    }, {});

    // Map employers to include their resources
    const mappedEmployers = employers.map((employer) => ({
      ...employer,
      resources: resourcesByEmployerBusinessId[employer.business_id] || [],
    }));

    const finalArray = [...mappedEmployers].filter(
      (employer) => employer?.resources.length > 0
    );

    // Return the final array of employers, including the fallback employer
    return finalArray;
  }
);

const countResourceCosts = (resources) => {
  return resources.map((resource) => {
    const wage = resource.wage || 0;
    const wageMultiplier = resource.wageMultiplier || 1;
    const cost = wage * wageMultiplier * resource.hours;

    return { ...resource, cost };
  });
};

const countEmployerHours = (employers, resources) => {
  const combinedEmployers = combineEmployersWithSameBusinessId(employers);
  return combinedEmployers.map((employer) => {
    const employerBusinessId = employer.business_id;
    const employerResources = resources.filter(
      (resource) => resource.employerBusinessId === employerBusinessId
    );
    const hours = employerResources.reduce(
      (acc, resource) => acc + resource.hours,
      0
    );
    const cost = employerResources.reduce(
      (acc, resource) => acc + resource.cost,
      0
    );

    return { ...employer, hours, cost };
  });
};

const combineEmployersWithSameBusinessId = (employers) => {
  return employers.reduce((acc, employer) => {
    const existingEmployer = acc.find(
      (existingEmployer) =>
        existingEmployer.business_id === employer.business_id
    );

    if (existingEmployer) {
      existingEmployer.hours += employer.hours;
      existingEmployer.cost += employer.cost;
    } else {
      acc.push({ ...employer });
    }

    return acc;
  }, []);
};

export const selectResourceTreeTotals = createSelector(
  [selectEmployerResourceTree],
  (resourceTree) => {
    if (resourceTree.length) {
      const totalHours = resourceTree.reduce((acc, employer) => {
        return acc + employer.hours;
      }, 0);
      const totalCost = resourceTree.reduce((acc, employer) => {
        return acc + employer.cost;
      }, 0);
      const totalResourceAmount = resourceTree.reduce((acc, employer) => {
        return acc + employer.resources.length;
      }, 0);

      return {
        hours: totalHours,
        cost: totalCost,
        amount: totalResourceAmount,
      };
    }
  }
);

export const selectEmployerResourceTreeTotals = (businessId) =>
  createSelector([selectEmployerResourceTree], (resourceTree) => {
    if (resourceTree.length) {
      const filteredResourceTree = resourceTree.filter(
        (employer) => employer.business_id === businessId
      );
      const totalHours = filteredResourceTree.reduce((acc, employer) => {
        return acc + employer.hours;
      }, 0);
      const totalCost = filteredResourceTree.reduce((acc, employer) => {
        return acc + employer.cost;
      }, 0);
      const totalResourceAmount = filteredResourceTree.reduce(
        (acc, employer) => {
          return acc + employer.resources.length;
        },
        0
      );

      return {
        hours: totalHours,
        cost: totalCost,
        amount: totalResourceAmount,
      };
    }
  });
